import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n/i18n";
import { addVendor } from "../../../../redux/Slices/authSlice";
import { toast } from "react-toastify";
import { makeOfflineRequest } from "../../../../shared/utils.js/offlineRequestManager";
import { applyFieldFilter } from "../../../../shared/utils.js/fieldValidation";

const AddVendor = () => {
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading } = useSelector(state => state.auth);
  const isRTL = i18n.language === "ar";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMaxWidth, setFormMaxWidth] = useState("700px");

  const [formData, setFormData] = useState({
    name_en: "",
    name_ar: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    tax_number: "",
  });

  // Sidebar responsive effect
  useEffect(() => {
    const updateFormWidth = () => {
      const root = document.documentElement;
      const sidebar =
        document.querySelector("[data-sidebar]") ||
        document.querySelector(".sidebar") ||
        document.querySelector('[class*="sidebar"]');

      if (sidebar) {
        const isCollapsed =
          sidebar.classList.contains("collapsed") ||
          sidebar.getAttribute("data-collapsed") === "true" ||
          window.getComputedStyle(sidebar).width === "0px" ||
          sidebar.offsetWidth < 100;

        if (isCollapsed) {
          root.style.setProperty("--form-max-width", "100%");
          setFormMaxWidth("100%");
        } else {
          root.style.setProperty("--form-max-width", "750px");
          setFormMaxWidth("750px");
        }
      }
    };

    updateFormWidth();

    const observer = new MutationObserver(updateFormWidth);
    const sidebar =
      document.querySelector("[data-sidebar]") ||
      document.querySelector(".sidebar") ||
      document.querySelector('[class*="sidebar"]');
    if (sidebar) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ["class", "data-collapsed"],
      });
    }

    window.addEventListener("resize", updateFormWidth);
    window.addEventListener("sidebar-toggle", updateFormWidth);
    document.addEventListener("sidebar-collapsed", updateFormWidth);
    document.addEventListener("sidebar-expanded", updateFormWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateFormWidth);
      window.removeEventListener("sidebar-toggle", updateFormWidth);
      document.removeEventListener("sidebar-collapsed", updateFormWidth);
      document.removeEventListener("sidebar-expanded", updateFormWidth);
    };
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    const finalValue =
      type === "checkbox" ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for offline support
      const formDataObj = new FormData();
      formDataObj.append("name_en", formData.name_en);
      formDataObj.append("name_ar", formData.name_ar);
      if (formData.contact_person)
        formDataObj.append("contact_person", formData.contact_person);
      if (formData.email) formDataObj.append("email", formData.email);
      if (formData.phone) formDataObj.append("phone", formData.phone);
      if (formData.address) formDataObj.append("address", formData.address);
      if (formData.tax_number)
        formDataObj.append("tax_number", formData.tax_number);

      try {
        // Try to use offline request manager first
        // ...existing code...
        const result = await makeOfflineRequest(
          "/vendors",
          "POST",
          formDataObj,
        );
        // ...existing code...
        setIsSubmitting(false);
        toast.success(
          result?.message ||
            t("vendors.add.success", { defaultValue: "تم إضافة المورد بنجاح" }),
          { rtl: isRTL },
        );
        setTimeout(() => {
          navigate("/vendors");
        }, 1500);
      } catch (offlineError) {
        // ...existing code...
        // ...existing code...
        // ...existing code...
        setIsSubmitting(false);
        // ...existing code...

        // If request was saved offline, show appropriate message
        if (offlineError.message === "OFFLINE_REQUEST_SAVED") {
          // ...existing code...
          toast.info(
            isRTL
              ? "تم حفظ الطلب وسيتم إرساله تلقائياً عند عودة الاتصال بالإنترنت"
              : "Request saved and will be sent automatically when connection is restored",
            { rtl: isRTL, autoClose: 5000 },
          );
          // ...existing code...
          // Still navigate away since the request is saved
          setTimeout(() => {
            navigate("/vendors");
          }, 2000);
          return;
        }

        // ...existing code...
        // If it's a different error, try the original Redux dispatch as fallback
        setIsSubmitting(true);
        await dispatch(addVendor(formData)).unwrap();
        setIsSubmitting(false);
        toast.success(
          t("vendors.add.success", { defaultValue: "تم إضافة المورد بنجاح" }),
          { rtl: isRTL },
        );
        setTimeout(() => {
          navigate("/vendors");
        }, 1500);
      }
    } catch (error) {
      setIsSubmitting(false);
      const errorData = error?.response?.data || error;
      let errorMessage = t("vendors.add.error", {
        defaultValue: isRTL ? "فشل إضافة المورد" : "Failed to add vendor",
      });

      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData?.errors) {
        errorMessage = Object.values(errorData.errors).flat().join(", ");
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { rtl: isRTL });
    }
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        style={{
          marginBottom: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: formMaxWidth,
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <h1 style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
          {t("vendors.add.title")}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/vendors")}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "10px 18px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {t("btns.back", { defaultValue: isRTL ? "رجوع" : "Back" })}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: "32px",
          width: "100%",
          maxWidth: formMaxWidth,
          textAlign: isRTL ? "right" : "left",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          {isRTL ? (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.add.nameAr")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "rtl",
                    textAlign: "right",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.add.nameEn")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "ltr",
                    textAlign: "left",
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.add.nameEn")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "ltr",
                    textAlign: "left",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {t("vendors.add.nameAr")}{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    direction: "rtl",
                    textAlign: "right",
                  }}
                />
              </div>
            </>
          )}

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.add.contactPerson")}
            </label>
            <input
              type="text"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.add.email")}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: "ltr",
                textAlign: "left",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.add.phone")}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.add.taxNumber")}
            </label>
            <input
              type="text"
              name="tax_number"
              value={formData.tax_number}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>

          <div style={{ gridColumn: "1 / span 2" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              {t("vendors.add.address")}
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: isRTL ? "right" : "left",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            marginTop: "24px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/vendors")}
            style={{
              padding: "12px 24px",
              backgroundColor: "#333",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {t("vendors.add.cancel")}
          </button>
          <button
            type="submit"
            disabled={isLoading || isSubmitting}
            style={{
              padding: "12px 24px",
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: isLoading || isSubmitting ? "not-allowed" : "pointer",
              opacity: isLoading || isSubmitting ? 0.6 : 1,
            }}
          >
            {isLoading || isSubmitting
              ? t("vendors.add.saving")
              : t("vendors.add.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddVendor;
